/*
 * <rf-vue-3d> — coque en 3D qui tourne au défilement (dos → côté → face → dos).
 *
 * - Three.js est chargé à la demande depuis jsDelivr, seulement quand la section approche de l'écran.
 * - La coque est construite dans le code (pas de fichier 3D) : couleur, forme du module caméra
 *   (même correspondance que snippets/module-camera.liquid), initiales et finition suivent la fiche.
 * - Sur la fiche produit, les pastilles et la liste de modèles de la section cliquent le vrai
 *   sélecteur de variante ; ailleurs (accueil), elles ne changent que la 3D.
 * - Glisser horizontalement fait tourner la coque, qui garde son angle au relâchement.
 *   Mouvement réduit : pas de rotation au défilement.
 * - Rendu seulement quand la section est visible ; image de repli si WebGL n'est pas disponible.
 */
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';

/** @type {Promise<any> | null} */
let threePromise = null;
const loadThree = () => (threePromise ??= import(THREE_URL));

/** Même correspondance que snippets/module-camera.liquid et cameraFor() dans initiales.js. */
function cameraFor(model) {
  if (model.includes('Air')) return 'air';
  if (model.includes('17 Pro')) return 'plateau';
  if (model.includes('Pro')) return 'tri';
  if (model.includes('16') || model.includes('17')) return 'pill';
  return 'diag';
}

/** Gabarit (largeur, hauteur, épaisseur) de la coque en unités de scène (1 unité ≈ 10 cm). */
function sizeFor(model) {
  if (model.includes('Air')) return { w: 0.78, h: 1.6, d: 0.085 };
  if (model.includes('Pro Max')) return { w: 0.81, h: 1.66, d: 0.11 };
  if (model.includes('17')) return { w: 0.75, h: 1.53, d: 0.11 };
  return { w: 0.75, h: 1.51, d: 0.11 };
}

/*
 * Modules caméra, dans le repère du dessin SVG du bloc Initiales (coque 180 × 400 en x=20, y=10).
 * rect = [x, y, largeur, hauteur, arrondi] ; lens / flash / dot = [cx, cy, rayon].
 */
const CAMERAS = {
  diag: { rect: [36, 26, 82, 82, 22], lens: [[59, 49, 15], [95, 85, 15]], flash: [[96, 48, 6]], dot: [[58, 88, 2.5]] },
  tri: { rect: [36, 26, 82, 82, 22], lens: [[58, 48, 14], [58, 86, 14], [94, 67, 14]], flash: [[97, 40, 5]], dot: [[97, 94, 5]] },
  pill: { rect: [38, 26, 42, 90, 21], lens: [[59, 48, 14], [59, 94, 14]], flash: [[94, 60, 6]], dot: [] },
  plateau: { rect: [28, 24, 164, 88, 24], lens: [[54, 47, 14], [54, 89, 14], [88, 68, 14]], flash: [[170, 46, 6]], dot: [[170, 86, 5]] },
  air: { rect: [28, 24, 164, 52, 26], lens: [[56, 50, 15]], flash: [[168, 50, 6]], dot: [[150, 50, 2.5]] },
};

/* Étapes de la rotation au défilement : progression → angle Y (dos, côté, face, dos). */
const KEYS = [
  [0, -0.45],
  [0.3, Math.PI / 2 - 0.3],
  [0.62, Math.PI],
  [1, 2 * Math.PI - 0.45],
];

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const smooth = (t) => t * t * (3 - 2 * t);

function angleAt(p) {
  for (let i = 1; i < KEYS.length; i++) {
    const [p1, a1] = KEYS[i];
    const [p0, a0] = KEYS[i - 1];
    if (p <= p1) return a0 + (a1 - a0) * smooth((p - p0) / (p1 - p0));
  }
  return KEYS[KEYS.length - 1][1];
}

class RfVue3d extends HTMLElement {
  #state = { color: '#151515', model: '', initials: '', finish: 'gold' };
  #colors = /** @type {Record<string, string>} */ ({});
  #placeholder = 'CARO';
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #touch = window.matchMedia('(pointer: coarse)').matches;
  #visible = false;
  #raf = 0;
  #rotY = KEYS[0][1];
  #drag = { active: false, x: 0, offset: 0 };
  /** @type {any} */ #t = null;
  /** @type {any} */ #three = null;
  /** @type {AbortController} */ #abort = new AbortController();
  /** @type {IntersectionObserver | null} */ #observer = null;

  connectedCallback() {
    this.#abort = new AbortController();
    try {
      this.#colors = JSON.parse(this.querySelector('[data-colors]')?.textContent || '{}');
    } catch {
      this.#colors = {};
    }
    this.#placeholder = this.dataset.placeholder || 'CARO';
    this.#state.model = this.dataset.model || '';
    this.#state.color = this.#colors[this.dataset.color || ''] || this.#state.color;
    this.#state.initials = this.#readInitials();
    this.#state.finish = document.querySelector('input[data-finish]:checked')?.getAttribute('data-finish') || 'gold';
    if (this.#reduced) this.classList.add('is-static');

    // Téléphone faible ou mode économie de données : la photo remplace la 3D (pas de téléchargement).
    const nav = /** @type {any} */ (navigator);
    if (nav.connection?.saveData || (nav.deviceMemory && nav.deviceMemory <= 2)) {
      this.classList.add('is-static', 'is-lite');
      this.#syncUi();
      return;
    }

    const signal = this.#abort.signal;
    document.addEventListener('change', this.#onChange, { signal });
    document.addEventListener('input', this.#onInput, { signal });
    // Capture : sur ordinateur, le thème fait défiler .page-wrapper et non la fenêtre.
    document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true, signal });
    window.addEventListener('resize', this.#onResize, { passive: true, signal });
    this.querySelectorAll('[data-swatch]').forEach((b) => b.addEventListener('click', this.#onSwatch, { signal }));
    this.querySelector('[data-model-select]')?.addEventListener('change', this.#onModelSelect, { signal });

    const canvasWrap = this.querySelector('[data-canvas]');
    canvasWrap?.addEventListener('pointerdown', this.#onPointerDown, { signal });
    window.addEventListener('pointermove', this.#onPointerMove, { passive: true, signal });
    window.addEventListener('pointerup', this.#onPointerUp, { signal });
    window.addEventListener('pointercancel', this.#onPointerUp, { signal });

    // Chargement paresseux : Three.js n'est téléchargé que quand la section approche.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#visible = entry.isIntersecting;
          if (entry.isIntersecting) this.#start();
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.#observer.observe(this);
    this.#syncUi();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#raf);
    this.#t?.renderer.dispose();
  }

  /* ---------- Lecture de la fiche produit ---------- */

  #readInitials() {
    const field = /** @type {HTMLInputElement | null} */ (
      document.querySelector('rf-initiales [data-input]') || document.querySelector('.rf-hero__try-input')
    );
    return field?.value?.trim() || '';
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('rf-initiales [data-input], .rf-hero__try-input')) return;
    this.#state.initials = target.value.trim();
    this.#rebuildText();
  };

  /** Changement de variante (boutons radio ou liste) ou de finition. */
  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (this.contains(target)) return;
    if (target instanceof HTMLInputElement && target.dataset.finish) {
      this.#state.finish = target.dataset.finish;
      this.#rebuildText();
      return;
    }
    let name = target.dataset.optionName || '';
    let value = target.value;
    if (target instanceof HTMLSelectElement) {
      const opt = target.selectedOptions[0];
      name = opt?.dataset.optionName || target.name || '';
      value = opt?.value || value;
    }
    if (this.dataset.colorOption && name.startsWith(this.dataset.colorOption)) this.#setColor(value);
    if (this.dataset.modelOption && name.startsWith(this.dataset.modelOption)) this.#setModel(value);
  };

  /** Pastille de la section : sur la fiche, on sélectionne la vraie variante. */
  /** @param {Event} event */
  #onSwatch = (event) => {
    const value = /** @type {HTMLElement} */ (event.currentTarget).dataset.swatch || '';
    if (!this.#clickVariant(this.dataset.colorOption, value)) this.#setColor(value);
  };

  /** @param {Event} event */
  #onModelSelect = (event) => {
    const value = /** @type {HTMLSelectElement} */ (event.target).value;
    if (!this.#clickVariant(this.dataset.modelOption, value)) this.#setModel(value);
  };

  /** Clique le bouton du sélecteur de variante de la fiche ; false s'il n'existe pas (accueil). */
  #clickVariant(option, value) {
    if (!option || this.dataset.linked !== 'true') return false;
    const input = [...document.querySelectorAll(`input[type="radio"][data-option-name="${CSS.escape(option)}"]`)].find(
      (el) => /** @type {HTMLInputElement} */ (el).value === value
    );
    if (input) {
      /** @type {HTMLInputElement} */ (input).click();
      return true;
    }
    const opt = [...document.querySelectorAll(`select option[data-option-name="${CSS.escape(option)}"]`)].find(
      (el) => /** @type {HTMLOptionElement} */ (el).value === value
    );
    const select = opt?.closest('select');
    if (opt && select) {
      select.value = /** @type {HTMLOptionElement} */ (opt).value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  #setColor(name) {
    const hex = this.#colors[name];
    if (!hex) return;
    this.#state.color = hex;
    this.dataset.color = name;
    this.#t?.bodyMat.color.set(hex);
    this.#t?.plateauMat.color.set(hex).multiplyScalar(0.82);
    this.#syncUi();
    this.#kick();
  }

  #setModel(model) {
    if (!model || model === this.#state.model) return;
    this.#state.model = model;
    this.#syncUi();
    this.#rebuildCase();
  }

  #syncUi() {
    this.querySelectorAll('[data-swatch]').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.getAttribute('data-swatch') === this.dataset.color))
    );
    const select = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-model-select]'));
    if (select && this.#state.model) select.value = this.#state.model;
    const label = this.querySelector('[data-model-label]');
    if (label) label.textContent = this.#state.model;
  }

  /* ---------- Three.js ---------- */

  async #start() {
    if (this.#three || this.dataset.failed) {
      this.#kick();
      return;
    }
    this.dataset.state = 'loading';
    try {
      this.#three = await loadThree();
      this.dataset.state = 'setup';
      this.#setup();
      this.classList.add('is-ready');
      this.dataset.state = 'ready';
      this.#onScroll();
      this.#kick();
    } catch (e) {
      this.dataset.failed = String(e);
      this.classList.add('is-fallback');
    }
  }

  #setup() {
    const THREE = this.#three;
    const wrap = /** @type {HTMLElement} */ (this.querySelector('[data-canvas]'));
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    // Écrans tactiles : résolution plafonnée à 1,5 (un écran ×3 coûte 4 fois plus à dessiner).
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.#touch ? 1.5 : 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;
    wrap.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 50);
    camera.position.set(0, 0, 4.6);

    // Studio lumineux pour les reflets (même principe que RoomEnvironment, sans module externe).
    const pmrem = new THREE.PMREMGenerator(renderer);
    const studio = new THREE.Scene();
    const panel = (w, h, x, y, z, color, intensity) => {
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(intensity), side: THREE.DoubleSide })
      );
      m.position.set(x, y, z);
      m.lookAt(0, 0, 0);
      studio.add(m);
    };
    studio.background = new THREE.Color(0x0b0b0b);
    panel(6, 3, 0, 4, 2, '#ffffff', 3.5);
    panel(4, 3, 0, 1, 5, '#ffffff', 1.2);
    panel(3, 5, -5, 1, 1, '#ffffff', 1.4);
    panel(3, 5, 5, 0, -1, '#f3d27a', 1.6);
    panel(8, 2, 0, -4, 0, '#2a2a2a', 1);
    scene.environment = pmrem.fromScene(studio, 0.04).texture;

    const key = new THREE.DirectionalLight(0xffffff, 2.4);
    key.position.set(2.5, 3, 4);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xd4af37, 2.2);
    rim.position.set(-3, 1.5, -3);
    scene.add(rim);
    const fill = new THREE.DirectionalLight(0xffffff, 0.9);
    fill.position.set(-3, -1, -4);
    scene.add(fill);
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));

    // Grain du simili : bruit dessiné une fois dans un canvas, utilisé en relief.
    const grain = document.createElement('canvas');
    grain.width = grain.height = 256;
    const g = /** @type {CanvasRenderingContext2D} */ (grain.getContext('2d'));
    const img = g.createImageData(256, 256);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = 110 + Math.random() * 145;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    const grainTex = new THREE.CanvasTexture(grain);
    grainTex.wrapS = grainTex.wrapT = THREE.RepeatWrapping;
    grainTex.repeat.set(6, 12);

    const bodyMat = new THREE.MeshStandardMaterial({
      color: this.#state.color,
      roughness: 0.5,
      metalness: 0.05,
      envMapIntensity: 1.4,
      bumpMap: grainTex,
      bumpScale: 0.6,
    });
    const plateauMat = bodyMat.clone();
    plateauMat.color.set(this.#state.color).multiplyScalar(0.82);
    const metalMat = new THREE.MeshStandardMaterial({ color: 0xd9d9d9, metalness: 1, roughness: 0.22 });
    const glassMat = new THREE.MeshPhysicalMaterial({ color: 0x050608, roughness: 0.05, metalness: 0.2, clearcoat: 1 });
    const screenMat = new THREE.MeshPhysicalMaterial({ color: 0x0b0c10, roughness: 0.06, metalness: 0.3, clearcoat: 1, envMapIntensity: 2 });
    const flashMat = new THREE.MeshStandardMaterial({ color: 0xf1e7c8, emissive: 0x6b5f3a, roughness: 0.3 });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.6 });

    const group = new THREE.Group();
    scene.add(group);

    this.#t = { renderer, scene, camera, group, bodyMat, plateauMat, metalMat, glassMat, screenMat, flashMat, darkMat, caseGroup: null, textMesh: null };
    this.#onResize();
    this.#rebuildCase();
  }

  /** Rectangle arrondi (centré) pour les formes extrudées. */
  #roundedRect(w, h, r) {
    const THREE = this.#three;
    const s = new THREE.Shape();
    const x = -w / 2;
    const y = -h / 2;
    r = Math.min(r, w / 2, h / 2);
    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y);
    s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + h - r);
    s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    s.lineTo(x + r, y + h);
    s.quadraticCurveTo(x, y + h, x, y + h - r);
    s.lineTo(x, y + r);
    s.quadraticCurveTo(x, y, x + r, y);
    return s;
  }

  #rebuildCase() {
    const t = this.#t;
    if (!t) return;
    const THREE = this.#three;
    if (t.caseGroup) {
      t.group.remove(t.caseGroup);
      t.caseGroup.traverse((o) => o.geometry?.dispose());
    }
    const { w, h, d } = sizeFor(this.#state.model);
    const bevel = 0.028;
    const caseGroup = new THREE.Group();

    // Coque : dos à z = +d/2 (face à la caméra au départ), écran à z = -d/2.
    const bodyGeo = new THREE.ExtrudeGeometry(this.#roundedRect(w - bevel * 2, h - bevel * 2, w * 0.17), {
      depth: d - bevel * 2,
      bevelEnabled: true,
      bevelThickness: bevel,
      bevelSize: bevel,
      bevelSegments: 8,
      curveSegments: 24,
    });
    bodyGeo.translate(0, 0, -(d - bevel * 2) / 2);
    caseGroup.add(new THREE.Mesh(bodyGeo, t.bodyMat));

    // Face avant : écran noir brillant, en retrait du rebord de la coque.
    const lip = 0.035;
    const screen = new THREE.Mesh(new THREE.ShapeGeometry(this.#roundedRect(w - lip * 2, h - lip * 2, w * 0.14), 24), t.screenMat);
    screen.position.z = -d / 2 - 0.001;
    screen.rotation.y = Math.PI;
    caseGroup.add(screen);

    // Repère SVG (coque 180 × 400 en x=20, y=10) → coordonnées 3D sur le dos.
    const sx = (x) => ((x - 110) / 180) * w;
    const sy = (y) => ((210 - y) / 400) * h;
    const k = w / 180;
    const back = d / 2;

    const cam = CAMERAS[cameraFor(this.#state.model)];
    const [rx, ry, rw, rh, rr] = cam.rect;
    const plateauDepth = 0.022;
    const plateau = new THREE.Mesh(
      new THREE.ExtrudeGeometry(this.#roundedRect(rw * k, rh * k, rr * k), {
        depth: plateauDepth,
        bevelEnabled: true,
        bevelThickness: 0.006,
        bevelSize: 0.006,
        bevelSegments: 4,
        curveSegments: 20,
      }),
      t.plateauMat
    );
    plateau.position.set(sx(rx + rw / 2), sy(ry + rh / 2), back);
    caseGroup.add(plateau);
    const top = back + plateauDepth + 0.006;

    for (const [cx, cy, r] of cam.lens) {
      const ring = new THREE.Mesh(new THREE.CylinderGeometry(r * k, r * k, 0.03, 40), t.metalMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.set(sx(cx), sy(cy), top + 0.012);
      const glass = new THREE.Mesh(new THREE.CylinderGeometry(r * k * 0.78, r * k * 0.78, 0.032, 40), t.glassMat);
      glass.rotation.x = Math.PI / 2;
      glass.position.set(sx(cx), sy(cy), top + 0.014);
      caseGroup.add(ring, glass);
    }
    for (const [cx, cy, r] of cam.flash) {
      const f = new THREE.Mesh(new THREE.CylinderGeometry(r * k, r * k, 0.01, 24), t.flashMat);
      f.rotation.x = Math.PI / 2;
      f.position.set(sx(cx), sy(cy), top + 0.002);
      caseGroup.add(f);
    }
    for (const [cx, cy, r] of cam.dot) {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(r * k, r * k, 0.01, 20), t.darkMat);
      m.rotation.x = Math.PI / 2;
      m.position.set(sx(cx), sy(cy), top + 0.002);
      caseGroup.add(m);
    }

    // Boutons recouverts sur les côtés, et ouverture du port en bas.
    const button = (len, y, side) => {
      const b = new THREE.Mesh(new THREE.BoxGeometry(0.02, len, d * 0.42), t.bodyMat);
      b.position.set(side * (w / 2 + 0.004), y, 0);
      caseGroup.add(b);
    };
    button(0.07, h * 0.3, -1);
    button(0.14, h * 0.18, -1);
    button(0.14, h * 0.07, -1);
    button(0.2, h * 0.2, 1);
    const port = new THREE.Mesh(new THREE.BoxGeometry(w * 0.3, 0.02, d * 0.45), t.darkMat);
    port.position.set(0, -h / 2 - 0.002, 0);
    caseGroup.add(port);

    t.caseGroup = caseGroup;
    t.back = back;
    t.w = w;
    t.h = h;
    t.group.add(caseGroup);
    t.textMesh = null;
    this.#rebuildText();
  }

  /** Initiales gravées : texture dessinée dans un canvas, rendue métallique (or ou argent). */
  #rebuildText() {
    const t = this.#t;
    if (!t?.caseGroup) return;
    const THREE = this.#three;
    const text = this.#state.initials || this.#placeholder;
    const silver = this.#state.finish === 'silver';

    const c = document.createElement('canvas');
    c.width = 1024;
    c.height = 256;
    const ctx = /** @type {CanvasRenderingContext2D} */ (c.getContext('2d'));
    const family = getComputedStyle(document.documentElement).getPropertyValue('--font-heading--family').trim() || 'serif';
    ctx.font = `700 150px ${family}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if ('letterSpacing' in ctx) ctx.letterSpacing = '22px';
    const grad = ctx.createLinearGradient(0, 40, 1024, 216);
    const stops = silver ? ['#8c8c8c', '#f7f7f7', '#c9c9c9', '#7a7a7a'] : ['#9c7a22', '#f6e39a', '#d4af37', '#8a6a1c'];
    [0, 0.45, 0.7, 1].forEach((o, i) => grad.addColorStop(o, stops[i]));
    ctx.fillStyle = grad;
    ctx.globalAlpha = this.#state.initials ? 1 : 0.55;
    ctx.fillText(text, 512, 134);

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    // Légère émission : sans elle, le métal paraît gris hors des reflets.
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      emissiveMap: tex,
      emissive: 0xffffff,
      emissiveIntensity: 0.45,
      transparent: true,
      metalness: 0.6,
      roughness: 0.28,
      depthWrite: false,
    });

    if (t.textMesh) {
      t.textMesh.material.map?.dispose();
      t.textMesh.material.dispose();
      t.textMesh.material = mat;
    } else {
      const width = t.w * 0.82;
      t.textMesh = new THREE.Mesh(new THREE.PlaneGeometry(width, width / 4), mat);
      // Même hauteur que dans l'aperçu 2D (y = 262 sur 420).
      t.textMesh.position.set(0, ((210 - 250) / 400) * t.h, t.back + 0.002);
      t.caseGroup.add(t.textMesh);
    }
    this.#kick();
  }

  /* ---------- Animation ---------- */

  #progress() {
    if (this.#reduced) return 0;
    const track = /** @type {HTMLElement} */ (this.querySelector('[data-track]') || this);
    const rect = track.getBoundingClientRect();
    const stage = /** @type {HTMLElement | null} */ (this.querySelector('.rf-vue3d__stage'));
    const run = rect.height - (stage?.offsetHeight || window.innerHeight);
    return run > 0 ? clamp(-rect.top / run, 0, 1) : 0;
  }

  #onScroll = () => {
    if (!this.#visible) return;
    const p = this.#progress();
    this.querySelectorAll('[data-step]').forEach((el) => {
      const [a, b] = /** @type {string} */ (el.getAttribute('data-step')).split('-').map(Number);
      el.classList.toggle('is-active', p >= a && p < b);
    });
    this.#kick();
  };

  #onResize = () => {
    const t = this.#t;
    if (!t) return;
    const wrap = /** @type {HTMLElement} */ (this.querySelector('[data-canvas]'));
    const { width, height } = wrap.getBoundingClientRect();
    t.renderer.setSize(width, height, false);
    t.camera.aspect = width / Math.max(height, 1);
    // Recule la caméra sur les écrans étroits pour garder la coque entière.
    t.camera.position.z = t.camera.aspect < 0.75 ? 5.4 : 4.6;
    t.camera.updateProjectionMatrix();
    this.#kick();
  };

  /** @param {PointerEvent} e */
  #onPointerDown = (e) => {
    this.#drag.active = true;
    this.#drag.x = e.clientX;
  };

  /** @param {PointerEvent} e */
  #onPointerMove = (e) => {
    if (!this.#drag.active) return;
    this.#drag.offset += (e.clientX - this.#drag.x) * 0.012;
    this.#drag.x = e.clientX;
    this.#kick();
  };

  #onPointerUp = () => {
    this.#drag.active = false;
  };

  /** Relance la boucle de rendu ; elle s'arrête seule quand tout est immobile. */
  #kick() {
    if (!this.#t || this.#raf) return;
    this.#raf = requestAnimationFrame(this.#frame);
  }

  #frame = (time) => {
    this.#raf = 0;
    const t = this.#t;
    if (!t || !this.#visible) return;

    // La rotation au doigt est gardée au relâchement : elle s'ajoute à celle du défilement.
    const target = angleAt(this.#progress()) + this.#drag.offset;
    this.#rotY += (target - this.#rotY) * 0.12;
    const float = this.#reduced ? 0 : Math.sin(time / 1400) * 0.025;

    t.group.rotation.y = this.#rotY;
    t.group.rotation.x = 0.12 + float * 0.6;
    t.group.position.y = float;
    t.renderer.render(t.scene, t.camera);

    const moving = Math.abs(target - this.#rotY) > 0.0005;
    // Petit flottement continu tant que la section est à l'écran (sauf mouvement réduit).
    if (moving || !this.#reduced) this.#raf = requestAnimationFrame(this.#frame);
  };
}

if (!customElements.get('rf-vue-3d')) customElements.define('rf-vue-3d', RfVue3d);
